import {
  conditionSourceKey,
  type ConditionSource,
} from "#bb/lib/conditionalRules";
import {
  clearStoredComponentStates,
  storeConditionValue,
  type ResourceConditionValues,
} from "#bb/lib/conditionValues";
import type { MaybeRefOrGetter } from "vue";

export function useConditionValues(
  resourceKey: MaybeRefOrGetter<string>,
) {
  const valuesByResource = useState<Record<string, ResourceConditionValues>>(
    "conditional-source-values",
    () => ({}),
  );

  function currentValues(): ResourceConditionValues {
    const key = toValue(resourceKey) || "default";
    return valuesByResource.value[key]
      ?? (valuesByResource.value[key] = {});
  }

  function setValue(source: ConditionSource, value: unknown) {
    return storeConditionValue(
      currentValues(),
      conditionSourceKey(source),
      value,
    );
  }

  function setComponentState(
    blockId: string,
    state: Record<string, unknown>,
  ) {
    for (const [field, value] of Object.entries(state)) {
      setValue({ type: "component-state", blockId, field }, value);
    }
  }

  function removeComponentState(blockId: string, fields?: string[]) {
    const values = currentValues();
    if (fields?.length) {
      for (const field of fields) {
        delete values[conditionSourceKey({
          type: "component-state",
          blockId,
          field,
        })];
      }
      return;
    }

    const prefix = `component-state:${blockId}:`;
    for (const key of Object.keys(values)) {
      if (key.startsWith(prefix)) delete values[key];
    }
  }

  function resolveValue(source: ConditionSource): unknown {
    return currentValues()[conditionSourceKey(source)]?.value;
  }

  function isAvailable(source: ConditionSource): boolean {
    return currentValues()[conditionSourceKey(source)]?.available === true;
  }

  function clearResource() {
    delete valuesByResource.value[toValue(resourceKey) || "default"];
  }

  function clearComponentStates() {
    return clearStoredComponentStates(currentValues());
  }

  return {
    setValue,
    setComponentState,
    removeComponentState,
    resolveValue,
    isAvailable,
    clearComponentStates,
    clearResource,
  };
}

export function useComponentConditionState(
  blockId: MaybeRefOrGetter<string>,
  state: MaybeRefOrGetter<Record<string, unknown>>,
  options: { clearBlockOnUnmount?: boolean } = {},
) {
  const features = inject("render-features", {
    resourceKey: "default",
  }) as { resourceKey?: string };
  const resourceKey = features.resourceKey || "default";
  const conditionValues = useConditionValues(resourceKey);
  let publishedFields: string[] = [];

  watchEffect(() => {
    const id = toValue(blockId);
    const nextState = toValue(state);
    const nextFields = Object.keys(nextState);
    const removedFields = publishedFields.filter(
      (field) => !nextFields.includes(field),
    );
    if (removedFields.length) {
      conditionValues.removeComponentState(id, removedFields);
    }
    conditionValues.setComponentState(id, nextState);
    publishedFields = nextFields;
  });

  onBeforeUnmount(() => {
    if (options.clearBlockOnUnmount) {
      conditionValues.removeComponentState(toValue(blockId));
    }
  });
}
